import { blue, gold, green, grey, red } from '@ant-design/colors';
import { LoadingOutlined, PhoneFilled, WarningFilled } from '@ant-design/icons';
import { Badge, Button, Popover, theme } from 'antd';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSWRConfig } from 'swr';
import { GrispiEvents } from '../../EventAndActionNames';
import useAgentStatusOptions from '../../hooks/agentStatus/useAgentStatusOptions';
import useTicketKey from '../../hooks/useTicketKey';
import useToggle from '../../hooks/useToggle';
import {
    agentStatusIdType,
    callClientStatusToCallPluginStatusMap,
    callPluginStatusMap,
    callPluginStatusType
} from '../../model/types';
import { CallTicketPostData, createCallComment, createCallTicket } from '../../services/tickets/tickets';
import { grispiNotification } from '../../store/useNotificationStore';
import { getCurrentUser, getTenantId } from '../../utils/accountHelper';
import { pushTo } from '../../utils/routeHelper';
import { getPluginStore } from '../../utils/store/getStore';
import {
    getAgentCurrentPluginStatus,
    getAgentNewPluginStatus,
    getChatPlugin,
    hasPluginTypeAnyErrors,
    isAnyPluginInitializing,
    sendAgentStatusChangeEventToPlugins
} from '../../utils/store/pluginStore';
import { filterSWRKeys } from '../../utils/swr';
import CallCountButton from './CallCountButton';
import CallCountDownButton from './CallCountDownButton';
import PluginApp from './PluginApp';
import { chatAppStatusOptionType } from './chat/ChatApp';
import { CHAT_APP_UI_STATES } from './chat/types';
import { PluginType } from './types';

function waitForIframeToRender(pluginId: string, callback) {
    if (document.getElementById(pluginId) !== null) {
        callback();
    } else {
        setTimeout(waitForIframeToRender.bind(null, pluginId, callback), 10);
    }
}

export const UI_STATES = {
    online: 'online', // sipState(ONLINE), msgType(STATE_CHANGE)
    offline: 'offline', // sipState(OFFLINE), msgType(STATE_CHANGE)
    away: 'away', // sipState(AWAY), msgType(STATE_CHANGE)
    resting: 'resting', // sipState(RESTING), msgType(ENDED_CALL)
    inboundOnline: 'inboundOnline', // sipState(INBOUND_ONLINE), msgType(STATE_CHANGE)
    outboundOnline: 'outboundOnline', // sipState(OUTBOUND_ONLINE), msgType(STATE_CHANGE)
    initializingError: 'initializingError'
} as const;

export type UIStates = (typeof UI_STATES)[keyof typeof UI_STATES];

export const colorStateMap = {
    [UI_STATES.online]: green.primary,
    [UI_STATES.offline]: red.primary,
    [UI_STATES.away]: gold.primary,
    [UI_STATES.resting]: blue.primary,
    [UI_STATES.inboundOnline]: green.primary,
    [UI_STATES.outboundOnline]: green.primary,
    [UI_STATES.initializingError]: grey[0],
    undefined: grey.primary
};

export const onlineColor = colorStateMap[UI_STATES.online];
export const restingColor = colorStateMap[UI_STATES.resting];

const agentStatusIdAsPerChatStatusMap: Record<string, Record<string, agentStatusIdType>> = {
    [callPluginStatusMap.ONLINE]: {
        [CHAT_APP_UI_STATES.ONLINE]: 10,
        [CHAT_APP_UI_STATES.OFFLINE]: 2,
        [CHAT_APP_UI_STATES.AWAY]: 2 // to set chat plugin status to offline
    },
    [callPluginStatusMap.ONLINE_INBOUND]: {
        [CHAT_APP_UI_STATES.ONLINE]: 11,
        [CHAT_APP_UI_STATES.OFFLINE]: 3,
        [CHAT_APP_UI_STATES.AWAY]: 3 // to set chat plugin status to offline
    },
    [callPluginStatusMap.ONLINE_OUTBOUND]: {
        [CHAT_APP_UI_STATES.ONLINE]: 12,
        [CHAT_APP_UI_STATES.OFFLINE]: 4,
        [CHAT_APP_UI_STATES.AWAY]: 4 // to set chat plugin status to offline
    },
    [callPluginStatusMap.AFTER_CALL_OPS]: {
        [CHAT_APP_UI_STATES.ONLINE]: 13,
        [CHAT_APP_UI_STATES.OFFLINE]: 5,
        [CHAT_APP_UI_STATES.AWAY]: 5 // to set chat plugin status to offline
    },
    [callPluginStatusMap.OFFLINE]: {
        [CHAT_APP_UI_STATES.ONLINE]: 9,
        [CHAT_APP_UI_STATES.OFFLINE]: 6,
        [CHAT_APP_UI_STATES.AWAY]: 6 // to set chat plugin status to offline
    },
    [callPluginStatusMap.AWAY]: {
        [CHAT_APP_UI_STATES.ONLINE]: 9, // to set call plugin status to offline, should fire event
        [CHAT_APP_UI_STATES.OFFLINE]: 2, // to set call plugin status to offline, should fire event
        [CHAT_APP_UI_STATES.AWAY]: 7
    }
    // [UI_STATES.initializingError]: {
    //     [CHAT_APP_UI_STATES.ONLINE]: 9,
    //     [CHAT_APP_UI_STATES.OFFLINE]: 6,
    //     [CHAT_APP_UI_STATES.AWAY]: 7,
    // },
};

const shouldFireEventAgentStatusIds: chatAppStatusOptionType[] = [
    CHAT_APP_UI_STATES.ONLINE,
    CHAT_APP_UI_STATES.OFFLINE
];

const deriveAgentStatusIdAsPerAgentChatStatus = (callStatusToChange: callPluginStatusType): agentStatusIdType => {
    if (
        !('pluginId' in getChatPlugin()) ||
        isAnyPluginInitializing(PluginType.CHAT) ||
        hasPluginTypeAnyErrors(PluginType.CHAT)
    ) {
        if (callStatusToChange === 'AWAY')
            return agentStatusIdAsPerChatStatusMap[callStatusToChange][CHAT_APP_UI_STATES.AWAY];

        // chat plugin should be assumed as 'OFFLINE' to set currentAgentStatusId
        return agentStatusIdAsPerChatStatusMap[callStatusToChange][callPluginStatusMap.OFFLINE];
    }

    const agentNewChatStatus = getAgentNewPluginStatus('chat') ?? getAgentCurrentPluginStatus('chat');

    const shouldFireEventToCallPlugin =
        callStatusToChange === 'AWAY' && shouldFireEventAgentStatusIds.includes(agentNewChatStatus);

    if (shouldFireEventToCallPlugin) {
        throw new Error(
            `Agent cannot have AWAY status for Call App for this option. Call App will be set to OFFLINE, explicitly`
        );
    }

    return agentStatusIdAsPerChatStatusMap[callStatusToChange][agentNewChatStatus];
};

const CallApp = ({ pluginId }: { pluginId: string }): JSX.Element => {
    const {
        token: { fontSizeIcon, zIndexBase }
    } = theme.useToken();

    const ticketKey = useTicketKey();
    const { mutate } = useSWRConfig();
    const { data: agentStatusOptions } = useAgentStatusOptions();

    const [popoverVisible, togglePopover, openPopover] = useToggle(false);
    const [incomingCallRinging, setIncomingCallRinging] = useState(false);
    const [status, setStatus] = useState(undefined);
    const [inCall, setInCall] = useState(false);
    const audioEl = useRef(null);

    const previousStatus = useRef(status);
    useEffect(() => {
        previousStatus.current = status;
    }, [status]);

    const color = colorStateMap[status];

    const statusUpdateHandler = useCallback(
        async (e) => {
            if (!pluginId) return;
            if (!e?.detail?.status) return;
            if (!agentStatusOptions) return;

            const statusToChange = e.detail.status;
            setStatus(statusToChange);
            getPluginStore().setPluginIsInitializing(pluginId, PluginType.CALL, false); // FIXME only cancel initializing state of THIS plugin
            if (statusToChange === UI_STATES.initializingError) {
                // alotech plugin gives error, thus call plugin should be assumed as 'OFFLINE' to set currentAgentStatusId
                const newAgentStatusId = deriveAgentStatusIdAsPerAgentChatStatus('OFFLINE');
                getPluginStore().setCallPluginError(pluginId, UI_STATES.initializingError);
                getPluginStore().changeAgentStatusOverMobXStore(newAgentStatusId);
                return;
            }

            try {
                const newAgentStatusId = deriveAgentStatusIdAsPerAgentChatStatus(
                    callClientStatusToCallPluginStatusMap[statusToChange]
                );

                // trigger agentStatus change process
                getPluginStore().setCallPluginError(pluginId, null);
                getPluginStore().changeAgentStatusOverMobXStore(newAgentStatusId);
            } catch (error) {
                // agent current chat status is ('OFFLINE' || 'ONLINE') and call status is wanted to be set to 'AWAY', We want to set call status to be 'OFFLINE'
                grispiNotification('warning', error.message);
                const selectedAgentStatus = agentStatusOptions?.find(
                    (agentStatus) =>
                        agentStatus.id ===
                        agentStatusIdAsPerChatStatusMap[callPluginStatusMap.OFFLINE][
                            getAgentCurrentPluginStatus('chat')
                        ]
                );

                // no need response of below function because we're sure that call plugin is available here and status change will be triggered over call plugin with dispatched status change event
                sendAgentStatusChangeEventToPlugins(selectedAgentStatus);
                // getPluginStore().changeAgentStatusOverMobXStore function will be triggered after call plugin response is received
            }
        },
        [pluginId, agentStatusOptions]
    );

    const disableRinging = useCallback((_e) => setIncomingCallRinging(false), []);

    const showPluginHandler = useCallback(() => openPopover(), [openPopover]);

    const dialingHandler = useCallback(
        (e) => {
            disableRinging(e);
            showPluginHandler();
        },
        [disableRinging, showPluginHandler]
    );

    const startedCallHandler = useCallback(
        async (e) => {
            showPluginHandler();
            setInCall(true);
            disableRinging(e);

            const callData: CallTicketPostData | null = e?.detail?.callData;
            if (!callData) return;

            try {
                const currentUserId = getCurrentUser().id?.toString();
                if (callData.direction === 'OUTBOUND') {
                    callData.source = currentUserId;
                } else if (callData.direction === 'INBOUND') {
                    callData.destination = currentUserId;
                    // any inbound call sent to grispi-ui supposed to create new ticket
                    // call-plugin sends ticketKey with inbound-callData, below code intented to fix this
                    delete callData.ticketKey;
                }

                if (callData.ticketKey) {
                    await createCallComment(callData);

                    // since the current ticket detail is already updated by above request (i.e, adding new comment), new comment will be served to UI by the following request
                    mutate(filterSWRKeys([`/tickets/${ticketKey}?ui=true`, `/changes/${ticketKey}`])); // will broadcast a revalidation message globally to exact SWR hooks using this key
                } else {
                    const createdTicket = await createCallTicket(callData);
                    // TODO: performance, added for performance optimization fall-back later
                    getTenantId() === 'yolcu360'
                        ? window.open(
                              `${process.env.NEXT_PUBLIC_PROTOCOL_TYPE_HELPER}${getTenantId()}.${
                                  process.env.BRAND_NAME
                              }${process.env.DOMAIN_EXTENSION}/tickets/${createdTicket.key}`,
                              '_blank'
                          )
                        : pushTo(`/tickets/${createdTicket.key}`);
                }
            } catch (error) {
                return;
            }
        },
        [disableRinging, mutate, showPluginHandler, ticketKey]
    );

    const endedCallHandler = useCallback(
        (e) => {
            setInCall(false);
            disableRinging(e);
        },
        [disableRinging]
    );

    const incomingCallHandler = useCallback(
        (_e) => {
            setIncomingCallRinging(true);
            openPopover();
        },
        [openPopover]
    );

    // Events from inside the iFrame is dispatched to the document element on this window so that any component can listen them.
    useEffect(() => {
        if (typeof pluginId === 'string' && pluginId.trim().length > 0) {
            waitForIframeToRender(pluginId, () => {
                const iframeEl = document.getElementById(pluginId);
                if (iframeEl) {
                    iframeEl.addEventListener(GrispiEvents.CALL_EVENT_STATUS_SET, statusUpdateHandler);
                    iframeEl.addEventListener(GrispiEvents.CALL_EVENT_INCOMING, incomingCallHandler);
                    iframeEl.addEventListener(GrispiEvents.CALL_EVENT_STARTED, startedCallHandler);
                    iframeEl.addEventListener(GrispiEvents.CALL_EVENT_DIALING, dialingHandler);
                    iframeEl.addEventListener(GrispiEvents.CALL_EVENT_ENDED, endedCallHandler);
                    iframeEl.addEventListener(GrispiEvents.SHOW_PLUGIN_EVENT, showPluginHandler);
                } else {
                    console.warn('CallApp', `There is no iFrame with id '${pluginId}'`);
                }
            });
        }

        return () => {
            const iframeEl = document.getElementById(pluginId);
            if (iframeEl) {
                // Following event listeners are added to the iframeEl in appAddedCallback
                iframeEl.removeEventListener(GrispiEvents.CALL_EVENT_STATUS_SET, statusUpdateHandler);
                iframeEl.removeEventListener(GrispiEvents.CALL_EVENT_INCOMING, incomingCallHandler);
                iframeEl.removeEventListener(GrispiEvents.CALL_EVENT_STARTED, startedCallHandler);
                iframeEl.removeEventListener(GrispiEvents.CALL_EVENT_DIALING, dialingHandler);
                iframeEl.removeEventListener(GrispiEvents.CALL_EVENT_ENDED, endedCallHandler);
                iframeEl.removeEventListener(GrispiEvents.SHOW_PLUGIN_EVENT, showPluginHandler);
            }
        };
    }, [pluginId]);

    useEffect(() => {
        if (incomingCallRinging) {
            audioEl.current.play();
            //Only logic related to audio was here so Added the logic to decrease the audio to here. Not sure if it works.
            audioEl.current.volume = 1;
            setTimeout(function decreaseVolume() {
                if (audioEl.current.volume > 0.39) {
                    audioEl.current.volume -= 0.2;
                    setTimeout(decreaseVolume, 2000);
                } else {
                    audioEl.current.volume = 0.1;
                }
            }, 3000);
        } else {
            audioEl.current?.pause();
        }
    }, [audioEl, incomingCallRinging]);

    const phoneIcon = () => {
        if (status === undefined || status === UI_STATES.initializingError) {
            const badgeIcon =
                status === undefined ? null : <WarningFilled style={{ color: '#f5222d', background: 'white' }} />;
            return (
                <Badge count={badgeIcon} offset={[8, 20]}>
                    <PhoneFilled className={clsx(incomingCallRinging && 'ringing')} style={{ color }} />
                </Badge>
            );
        }

        return <PhoneFilled className={clsx(incomingCallRinging && 'ringing')} style={{ color }} />;
    };

    const renderToggleButton = () => {
        if (status === UI_STATES.resting) {
            return <CallCountDownButton togglePopover={togglePopover} />;
        }

        if (inCall) {
            return <CallCountButton togglePopover={togglePopover} />;
        }

        return (
            <div>
                {status === undefined && (
                    <div style={{ position: 'relative' }}>
                        <LoadingOutlined
                            style={{
                                position: 'absolute',
                                right: -(fontSizeIcon * 0.5),
                                bottom: -(fontSizeIcon * 3),
                                zIndex: zIndexBase + 1
                            }}
                        />
                    </div>
                )}
                <Button
                    icon={phoneIcon()}
                    style={{ backgroundColor: 'transparent', borderColor: color }}
                    onClick={togglePopover}
                />
            </div>
        );
    };

    const handleErrorCallback = useCallback(
        (status) => statusUpdateHandler({ detail: { status } }),
        [statusUpdateHandler]
    );

    return (
        <>
            <audio ref={audioEl} src="https://bigsoundbank.com/UPLOAD/ogg/1699.ogg" preload="auto" loop />
            <Popover
                content={
                    <PluginApp
                        pluginId={pluginId}
                        errorCallback={handleErrorCallback}
                        popoverVisible={popoverVisible}
                    />
                }
                trigger="click"
                placement="bottomRight"
                overlayClassName={clsx(!popoverVisible && 'not-visible')}
                open={true}
            >
                {renderToggleButton()}
            </Popover>
        </>
    );
};

export default memo(CallApp);
